import { Stack, Button, Group, Text, Textarea } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { UseMutationResult } from "@tanstack/react-query"
import { LendFormData } from "../types"
import { DatePickerInput } from "@mantine/dates"
import { InfinitePersonSingleSelect } from "@components/SelectFromQuery/InfinitePersonSingleSelect"
import { LocationSelect } from "@components/select/location-select"
import { DefaultModal } from "@components/default-modal"

interface LendModalProps {
  opened: boolean
  onClose: () => void
  lendFormData: LendFormData
  setLendFormData: (data: LendFormData) => void
  createBorrowMutation: UseMutationResult<any, Error, LendFormData>
  hasPersonOrLocation: boolean
  personIsRequired: boolean
  locationIsRequired: boolean
  formValidationError: string | null
}

export function LendModal({
  opened,
  onClose,
  lendFormData,
  setLendFormData,
  createBorrowMutation,
  hasPersonOrLocation,
  personIsRequired,
  locationIsRequired,
  formValidationError,
}: LendModalProps) {
  const { t } = useTranslation("app_server")

  return (
    <DefaultModal
      opened={opened}
      onClose={() => {
        onClose()
        createBorrowMutation.reset()
      }}
      title={t("Lend")}
    >
      {opened && (
        <Stack gap="md">
          <DatePickerInput
            label={t("StartDate")}
            value={lendFormData.startDate ?? undefined}
            onChange={(value) =>
              setLendFormData({
                ...lendFormData,
                startDate: value ? new Date(value) : null,
              })
            }
            required
          />

          <InfinitePersonSingleSelect
            value={lendFormData.personId}
            onSelectionChange={(value) =>
              setLendFormData({ ...lendFormData, personId: value })
            }
            required={personIsRequired}
          />

          <LocationSelect
            value={lendFormData.locationId}
            onChange={(value) =>
              setLendFormData({ ...lendFormData, locationId: value })
            }
            placeholder={t("SearchLocation")}
            required={locationIsRequired}
          />

          {formValidationError && !hasPersonOrLocation && (
            <Text color="red" size="sm">
              {formValidationError}
            </Text>
          )}

          <Textarea
            label={t("Note")}
            placeholder={t("EnterNote")}
            value={lendFormData.note}
            onChange={(e) =>
              setLendFormData({
                ...lendFormData,
                note: e.currentTarget.value,
              })
            }
          />

          <Group justify="flex-end" gap="md">
            <Button variant="default" onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button
              onClick={() => createBorrowMutation.mutate(lendFormData)}
              loading={createBorrowMutation.isPending}
              disabled={!hasPersonOrLocation}
            >
              {t("Lend")}
            </Button>
          </Group>
        </Stack>
      )}
    </DefaultModal>
  )
}
